import { useEffect, useState } from "react";
import { X, Search } from "lucide-react";
import api from "../lib/api";
import { TuscaniaPickerTab } from "./TuscaniaPickerTab";
import { fmtKc } from "../lib/format";

const fmt = fmtKc;
const TABS = [["catalog", "Ceník prací"], ["coatings", "Stěrky"], ["tiles", "Obklady a dlažby"], ["tuscania", "Tuscania"], ["budgets", "Z rozpočtu"]];

export function InvoiceItemPicker({ onAdd, onAddMany, onClose }) {
    const [tab, setTab] = useState("catalog");
    const [q, setQ] = useState("");
    const [catalog, setCatalog] = useState([]);
    const [coatings, setCoatings] = useState([]);
    const [tiles, setTiles] = useState([]);
    const [budgets, setBudgets] = useState([]);

    useEffect(() => {
        api.get("/catalog?sort=popular").then((r) => setCatalog(r.data)).catch(() => {});
        api.get("/coatings").then((r) => setCoatings(r.data)).catch(() => {});
        api.get("/tiles").then((r) => setTiles(r.data)).catch(() => {});
        api.get("/budgets").then((r) => setBudgets((r.data || []).filter((b) => !b.is_template))).catch(() => {});
    }, []);

    const rows = {
        catalog: catalog.map((c) => ({ key: c.id, name: c.name, description: c.description || "", sub: c.category || "", unit: c.unit || "ks", price: c.unit_price || 0 })),
        coatings: coatings.map((c) => ({ key: c.id, name: `Dekorativní stěrka — ${c.color}`, description: [c.manufacturer, c.effect].filter(Boolean).join(", "), sub: `${c.effect || "Nezařazeno"} · práce + materiál`, unit: "m²", price: c.price_per_m2 || 0 })),
        tiles: tiles.flatMap((t) => (t.variants || []).map((v) => ({ key: `${t.id}-${v.id}`, name: `${t.name} — ${[v.size, v.color].filter(Boolean).join(", ")}`, description: t.manufacturer ? `Výrobce: ${t.manufacturer}` : "", sub: t.category === "obklad" ? "Obklad" : "Dlažba", unit: v.unit || "m²", price: v.sell_price || 0 }))),
    }[tab] || [];
    const filtered = rows.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()));
    const filteredBudgets = budgets.filter((b) => !q || `${b.number} ${b.title} ${b.client_name}`.toLowerCase().includes(q.toLowerCase()));

    const importBudget = async (b) => {
        const { data } = await api.get(`/budgets/${b.id}`);
        onAddMany((data.items || []).map((it) => ({ name: it.name, description: it.description || "", quantity: it.quantity, unit: it.unit || "ks", unit_price: it.unit_price })), data);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="item-picker">
            <div className="bg-white border-2 border-slate-900 max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-6 py-4 border-b-2 border-slate-900">
                    <h2 className="font-display text-lg font-bold">Vložit položky</h2>
                    <button onClick={onClose} data-testid="item-picker-close"><X className="w-5 h-5 text-slate-400 hover:text-slate-900" /></button>
                </div>
                <div className="flex border-b border-slate-200 overflow-x-auto">
                    {TABS.map(([k, l]) => (
                        <button key={k} onClick={() => setTab(k)} data-testid={`picker-tab-${k}`}
                                className={`px-4 py-2.5 text-xs uppercase tracking-wider font-semibold whitespace-nowrap border-b-2 ${tab === k ? "border-orange-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}>{l}</button>
                    ))}
                </div>
                <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                    <Search className="w-4 h-4 text-slate-400" />
                    <input autoFocus placeholder="Hledat…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="item-picker-search" className="flex-1 text-sm focus:outline-none bg-transparent" />
                </div>
                <div className="overflow-y-auto">
                    {tab === "tuscania" ? <TuscaniaPickerTab q={q} onAdd={onAdd} /> : tab === "budgets" ? (
                        filteredBudgets.length === 0 ? <div className="p-8 text-center text-slate-500 text-sm">Žádné rozpočty.</div> :
                        filteredBudgets.map((b) => (
                            <button key={b.id} onClick={() => importBudget(b)} data-testid={`picker-budget-${b.id}`} className="w-full px-6 py-3 border-b border-slate-100 hover:bg-orange-50/50 text-left flex items-center justify-between gap-3">
                                <div><div className="font-medium text-slate-900"><span className="font-mono text-xs text-orange-700 mr-2">{b.number}</span>{b.title}</div><div className="text-xs text-slate-500">{b.client_name || "—"} · {b.status || ""}</div></div>
                                <div className="text-sm font-semibold tabular-data">{fmt(b.subtotal)}</div>
                            </button>
                        ))
                    ) : filtered.length === 0 ? <div className="p-8 text-center text-slate-500 text-sm">Nic nenalezeno.</div> : filtered.map((r) => (
                        <button key={r.key} onClick={() => onAdd({ name: r.name, description: r.description, quantity: 1, unit: r.unit, unit_price: r.price })} data-testid={`picker-item-${r.key}`}
                                className="w-full px-6 py-3 border-b border-slate-100 hover:bg-orange-50/50 text-left flex items-center justify-between gap-3">
                            <div className="min-w-0"><div className="font-medium text-slate-900 truncate">{r.name}</div><div className="text-xs text-slate-500">{r.sub}</div></div>
                            <div className="text-sm font-semibold tabular-data whitespace-nowrap">{fmt(r.price)} / {r.unit}</div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
